import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Search, FileText } from 'lucide-react';
import { getModuleLabel, MODULE_LABELS } from './promptModuleLabels';

const CATEGORIES = [
  { id: null, label: 'Kaikki' },
  ...Object.entries(MODULE_LABELS).map(([id, label]) => ({ id, label })),
];

const CATEGORY_ORDER = Object.keys(MODULE_LABELS);

const Container = styled.div`
  width: 320px;
  min-width: 320px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const SearchBox = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;

  input {
    flex: 1;
    background: none;
    border: none;
    color: #fff;
    font-size: 13px;
    outline: none;
    &::placeholder { color: #555; }
  }
  svg { color: #555; flex-shrink: 0; }
`;

const CategoryTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const CategoryTab = styled.button`
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 6px;
  border: 1px solid ${p => p.$active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.08)'};
  background: ${p => p.$active ? 'rgba(99, 102, 241, 0.2)' : 'transparent'};
  color: ${p => p.$active ? '#a5b4fc' : '#888'};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: rgba(99, 102, 241, 0.3); color: #a5b4fc; }
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const PromptCard = styled.button`
  width: 100%;
  text-align: left;
  padding: 12px;
  background: ${p => p.$active ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
  border: 1px solid ${p => p.$active ? 'rgba(99, 102, 241, 0.3)' : 'transparent'};
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 2px;

  &:hover {
    background: ${p => p.$active ? 'rgba(99, 102, 241, 0.15)' : 'rgba(255, 255, 255, 0.04)'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
  min-width: 0;
  line-height: 1.35;
`;

const ModuleBadge = styled.span`
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.25);
`;

const CardDescription = styled.div`
  font-size: 11px;
  color: #777;
  line-height: 1.45;
  margin-bottom: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardKey = styled.div`
  font-size: 10px;
  color: #555;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  margin-bottom: 4px;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #555;
`;

const Badge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.06);
  color: #888;
`;

const CountBadge = styled.span`
  font-size: 12px;
  color: #555;
  padding: 0 8px;
`;

function sortPrompts(a, b) {
  const catA = CATEGORY_ORDER.indexOf(a.category);
  const catB = CATEGORY_ORDER.indexOf(b.category);
  if (catA !== catB) return (catA === -1 ? 99 : catA) - (catB === -1 ? 99 : catB);
  return (a.name || a.key).localeCompare(b.name || b.key, 'fi');
}

const PromptList = ({ prompts, selectedKey, onSelect, search, onSearchChange, category, onCategoryChange }) => {
  const filtered = useMemo(() => {
    const list = prompts.filter(p => {
      if (category && p.category !== category) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          p.key?.toLowerCase().includes(q) ||
          p.name?.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          getModuleLabel(p.category).toLowerCase().includes(q)
        );
      }
      return true;
    });
    return [...list].sort(sortPrompts);
  }, [prompts, category, search]);

  return (
    <Container>
      <SearchBox>
        <SearchInput>
          <Search size={14} />
          <input
            placeholder="Hae prompteja..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
          />
          <CountBadge>{filtered.length}/{prompts.length}</CountBadge>
        </SearchInput>
      </SearchBox>

      <CategoryTabs>
        {CATEGORIES.map(cat => (
          <CategoryTab
            key={cat.id || 'all'}
            $active={category === cat.id}
            onClick={() => onCategoryChange(cat.id)}
          >
            {cat.label}
          </CategoryTab>
        ))}
      </CategoryTabs>

      <ListContainer>
        {filtered.map(p => (
          <PromptCard
            key={p.key}
            $active={p.key === selectedKey}
            onClick={() => onSelect(p.key)}
          >
            <CardHeader>
              <CardTitle>
                <FileText size={12} style={{ color: '#6366f1', flexShrink: 0, marginTop: 2 }} />
                <span>{p.name || p.key}</span>
              </CardTitle>
              <ModuleBadge>{getModuleLabel(p.category)}</ModuleBadge>
            </CardHeader>
            {p.description && <CardDescription>{p.description}</CardDescription>}
            <CardKey>{p.key}</CardKey>
            <CardMeta>
              <Badge>{p.model || 'sonnet'}</Badge>
              <span>v{p.version}</span>
              {p.overrides?.length > 0 && (
                <Badge
                  style={{ color: '#f59e0b' }}
                  title={`Oma versio: ${p.overrides.map(o => o.instanceTitle || o.instanceId).join(', ')}`}
                >
                  {p.overrides.length === 1 ? '1 oma versio' : `${p.overrides.length} omaa versiota`}
                </Badge>
              )}
            </CardMeta>
          </PromptCard>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: '#555', fontSize: 13 }}>
            Ei prompteja
          </div>
        )}
      </ListContainer>
    </Container>
  );
};

export default PromptList;
